<script lang="ts">
  import '../app.css';
  import 'virtual:fonts.css';
  import { theme } from '$lib/stores/theme';
  import { surveyStore } from '$lib/stores/surveyStore';
  import type { LayoutData } from './$types';
  import { onMount, onDestroy } from 'svelte';
  import { fly, fade } from 'svelte/transition';
  import { beforeNavigate, afterNavigate, goto } from '$app/navigation';
  import SharedLayout from '$lib/components/SharedLayout.svelte';
  import { interpretSurvey } from '$lib/utils/surveyInterpreter';
  import type { InterpretedSurvey } from '$lib/utils/surveyInterpreter';
  import SurveyWatcher from '$lib/components/SurveyWatcher.svelte';
  import { invalidateAll } from '$app/navigation';
  import { SurveyController } from '$lib/controllers/surveyController';
  import { page } from '$app/stores';
  import { routePatterns } from '$lib/routes';
  import SystemStatusBanner from '$lib/components/SystemStatusBanner.svelte';
  import { GdprConsentBanner, AnalyticsConsentBanner } from '@lib/components';
  import { checkGdprConsent } from '@lib/utils/gdprConsent';
  import { surveyInitializer } from '@lib/services/SurveyInitializer';
  import { analyticsService } from '@lib/services/AnalyticsService';
  import MetaTags from '$lib/components/MetaTags.svelte';
  import MetaPixel from '$lib/components/MetaPixel.svelte';
  import translations from '$lib/translations/survey-player.yaml';

  export let data: LayoutData;
  let mounted = false;
  let previewMode = false;
  let unsubscribe: () => void;
  let interpretedSurvey: InterpretedSurvey | undefined;
  let showGdprBanner = false;
  let showAnalyticsBanner = false;
  let navigating = false;
  let showContent = true;

  // Determine if we're on a main survey page where the banner should be shown
  // Include root, numeric routes, and exclude only specific non-survey routes
  // Only treat root and single-question routes as main survey routes
  $: isMainSurveyRoute = routePatterns.isMainSurveyRoute($page.url.pathname);

  // Don't show GDPR banner on policy pages - users need to read policies before consenting
  $: isPolicyRoute = $page.url.pathname.startsWith('/policies/');

  // Get the current page's survey data if available (from page-specific loaders)
  // Fall back to layout data for main survey routes
  $: currentPageData = $page.data?.survey || data.survey;

  // Reactively update GDPR banner visibility based on route and consent status
  // Use the consentPolicy from the current page's data, not the main survey
  $: {
    const consentPolicies = currentPageData?.config?.consentPolicy;
    const needsConsent = consentPolicies && (Array.isArray(consentPolicies) ? consentPolicies.length > 0 : true);
    const hasConsent = surveyInitializer.getConsentStatus();

    // Show banner if THIS page requires consent and user hasn't consented
    // Don't show on policy pages even if they have consentPolicy
    if (needsConsent && !hasConsent && !isPolicyRoute && mounted) {
      showGdprBanner = true;
    } else {
      showGdprBanner = false;
    }
  }

  // Get survey language
  $: surveyLanguage = data.survey?.config?.language || 'en_us';

  // Get system messages for connection errors in the survey's language
  $: currentTranslations = translations[surveyLanguage] || translations.en_us;
  $: connectionErrorMessages = {
    title: currentTranslations.connection_error_title,
    subtitle: currentTranslations.connection_error_subtitle,
    retryLabel: currentTranslations.connection_error_retry,
    // Allow override from survey config if provided
    ...($surveyStore?.survey?.config as any)?.system?.connectionError || {}
  };

  async function checkTokenWithConsent() {
    try {
      // Check if consent is even required
      const requiresConsent = data.survey?.config?.consentPolicy;

      // Only check consent status if consent is actually required
      if (requiresConsent && !surveyInitializer.getConsentStatus()) {
        // Queue the token check for after consent
        surveyInitializer.checkTokenWithConsent();
        return;
      }

      // Either consent is not required, or it has been granted
      // Check if we already have a valid token (passive verification)
      const state = $surveyStore;
      const hasToken = !!state?.middlewareData?.submissionId;
      const surveyId = state?.survey?.config?.id;

      // Only initialize if we don't have a token yet
      if (!hasToken && surveyId) {
        surveyStore.setTokenError(false);

        // Use initialize() which is idempotent - if +page.svelte already called it,
        // this will return the same promise without making a duplicate request
        try {
          await SurveyController.initialize(surveyId, state?.survey);

          const newState = $surveyStore;
          if (!newState?.middlewareData?.submissionId) {
            surveyStore.setTokenError(true);
          }
        } catch (error) {
          console.error('Failed to initialize survey:', error);
          surveyStore.setTokenError(true);
        }
      }
    } catch (error) {
      console.error('Failed to verify security token:', error);
      surveyStore.setTokenError(true);
    }
  }

  onMount(() => {
    mounted = true;
    showContent = true;

    // Subscribe to the survey store to get the preview mode state
    unsubscribe = surveyStore.subscribe(state => {
      previewMode = !!state?.previewMode;
    });
    
    // Determine if we're on a main survey page where token is needed
    const pathname = window.location.pathname;
    const isMainRoute = routePatterns.isMainSurveyRoute(pathname);
    
    // Handle survey data updates without requiring a page reload
    const handleSurveyUpdate = (event: CustomEvent) => {
      const updatedSurvey = event.detail.survey;
      if (updatedSurvey) {
        console.log('Layout received updated survey with theme:', updatedSurvey?.theme?.primaryColor);
        interpretedSurvey = updatedSurvey;
        
        // Trigger a reactive update for any components that depend on theme
        themeValue = updatedSurvey?.theme?.theme || $theme;
      }
    };
    
    // Add event listener for survey updates
    window.addEventListener('walku:survey-updated', handleSurveyUpdate as EventListener);
    
    // HMR handling for survey updates through Vite
    if (typeof window !== 'undefined' && import.meta.hot) {
      // Listen for custom HMR events from our plugin
      import.meta.hot.on('survey-update', (eventData) => {
        invalidateAll();
      });

      // Also try to handle updates to the survey.js file
      import.meta.hot.accept((newModule) => {
        invalidateAll();
      });
    }
    
    // Only reinterpret if necessary (this was causing theme issues)
    if (data.survey && !data.survey.theme?.primaryColor) {
      // The type error is due to the type definition expecting a Survey but actually
      // working with both Survey and InterpretedSurvey. Using any to bypass the check safely.
      interpretedSurvey = interpretSurvey(data.survey as any);
    } else {
      interpretedSurvey = data.survey as InterpretedSurvey;
    }
    
    // Check if tracking is completely disabled
    const disableTracking = data.survey?.config?.disableTracking === true;
    
    // Only check for token on main survey routes if tracking is enabled
    if (isMainRoute && !disableTracking) {
      // A short delay to allow the survey to initialize before checking the token
      setTimeout(checkTokenWithConsent, 100);
    }

    // GDPR banner visibility is now handled reactively above

    // Initialize analytics if consent was previously granted
    if (data.survey?.config?.enableAnalyticsTracking) {
      initializeAnalytics();
    }
    
    return () => {
      // Clean up survey update event listener
      window.removeEventListener('walku:survey-updated', handleSurveyUpdate as EventListener);
    };
  });

  // Routes that handle their own transitions
  const NO_TRANSITION_ROUTES = ['/s/', '/stats/', '/profile/'];

  function supportsTransition(url: URL | null | undefined): boolean {
    if (!url?.pathname) return true;
    return !NO_TRANSITION_ROUTES.some(route => url.pathname.startsWith(route));
  }

  // Handle smooth page transitions
  beforeNavigate(async ({ from, to }) => {
    const fromSupportsTransition = !from || supportsTransition(from.url);
    const toSupportsTransition = to && supportsTransition(to.url);

    if (fromSupportsTransition && toSupportsTransition) {
      navigating = true;
      showContent = false;
      // Wait for exit transition to complete
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  });

  afterNavigate(({ from, to }) => {
    const fromSupportsTransition = !from || supportsTransition(from.url);
    const toSupportsTransition = to && supportsTransition(to.url);

    if (fromSupportsTransition && toSupportsTransition) {
      navigating = false;
      // Small delay before showing content to ensure page is ready
      setTimeout(() => {
        showContent = true;
      }, 250);
    } else {
      // For survey routes and stats dashboard, ensure content is always visible
      showContent = true;
      navigating = false;
    }

    // Track page view for analytics if initialized
    if (to?.url.pathname && analyticsService.isInitialized()) {
      analyticsService.trackPageView(to.url.pathname);
    }
  });

  onDestroy(() => {
    if (unsubscribe) unsubscribe();
  });

  // Use the theme from data if available, or fallback to the theme store
  $: themeValue = data.survey?.theme?.theme || $theme;
  
  // Get current screen config to check for allowTextSelection
  $: currentScreenIndex = parseInt($page.params.questionId) || 0;
  $: currentScreen = interpretedSurvey?.screens?.[currentScreenIndex];
  $: allowTextSelection = currentScreen?.config?.allowTextSelection || false;
  
  // Apply no-text-select class only when not in preview mode and not explicitly allowed
  $: textSelectionClass = (previewMode || allowTextSelection) ? '' : 'no-text-select';

  // Only clear token error when navigating away from survey routes
  // Don't automatically set it - let the actual token request logic handle that
  $: if (!isMainSurveyRoute && $surveyStore?.tokenError) {
    // Clear error when navigating away from survey routes
    surveyStore.setTokenError(false);
  }

  // Check if tracking is disabled
  $: disableTracking = data.survey?.config?.disableTracking === true;

  // Analytics tracking config
  // Enabled only if: 1) survey config enables it AND 2) environment flag allows it
  $: analyticsEnvEnabled = import.meta.env.VITE_ANALYTICS_ENABLED !== 'false';
  $: enableAnalyticsTracking = data.survey?.config?.enableAnalyticsTracking === true && analyticsEnvEnabled;
  $: analyticsConsentConfig = data.survey?.config?.analyticsConsent;

  // Show analytics consent banner only when:
  // 1. Analytics tracking is enabled (enableAnalyticsTracking: true)
  // 2. User hasn't made a consent decision yet (needs to see banner)
  // 3. Not on a page with blocking GDPR consent (survey consent takes precedence)
  $: {
    if (mounted && enableAnalyticsTracking && !showGdprBanner) {
      showAnalyticsBanner = analyticsService.needsConsentDecision();
    } else {
      showAnalyticsBanner = false;
    }
  }

  async function handleTokenRetry() {
    // Temporarily hide the banner to show user we're retrying
    surveyStore.setTokenError(false);

    // Wait a moment for UI update
    await new Promise(resolve => setTimeout(resolve, 100));

    // Check if survey config exists
    const state = surveyStore.getState();

    if (!state?.survey?.config?.id) {
      // Re-initialize the survey if config is missing
      if (data.survey) {
        try {
          await SurveyController.initializeSurveyFromRawData(data.survey);
        } catch (error) {
          console.error('Failed to reinitialize survey:', error);
          // If reinitialization fails, show error again
          surveyStore.setTokenError(true);
          return;
        }
      } else {
        surveyStore.setTokenError(true);
        return;
      }
    }

    // Retry the token request
    try {
      await checkTokenWithConsent();

      // Check if we actually got a token
      const newState = surveyStore.getState();

      if (!newState?.middlewareData?.submissionId) {
        // Still no token, show error again
        surveyStore.setTokenError(true);
      }
    } catch (error) {
      console.error('Token retry failed:', error);
      // Retry failed, show error again
      surveyStore.setTokenError(true);
    }
  }

  async function handleGdprConsent() {
    showGdprBanner = false;

    // Check if tracking is disabled
    const disableTracking = data.survey?.config?.disableTracking === true;

    if (!disableTracking) {
      // Token check will be triggered automatically by the SurveyInitializer
      // The consent has already been granted by the banner component calling surveyInitializer.grantConsent()
      setTimeout(checkTokenWithConsent, 100);

      // If this is the main route, also trigger any deferred survey initialization
      if (isMainSurveyRoute) {
        // Force page reload to re-run initialization logic with consent granted
        window.location.reload();
      }
    }
  }

  // Initialize analytics if consent already granted
  function initializeAnalytics() {
    if (!enableAnalyticsTracking || !data.survey?.config?.id) return;

    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
    if (!apiBaseUrl) {
      console.error('[Analytics] VITE_API_BASE_URL not configured');
      return;
    }

    const domain = data.survey?.config?.deployment?.domain || window.location.hostname;

    const initialized = analyticsService.initialize({
      surveyId: data.survey.config.id,
      domain: domain,
      version: data.survey?.config?.version,
      apiEndpoint: `${apiBaseUrl}/analytics`
    });

    if (initialized) {
      // Track current page on initialization
      analyticsService.trackPageView(window.location.pathname);
    }
  }

  function handleAnalyticsAccept() {
    showAnalyticsBanner = false;
    initializeAnalytics();
  }

  function handleAnalyticsReject() {
    showAnalyticsBanner = false;
    // No action needed - sessionStorage rejection is handled by the banner component
  }

  // Evaluate config flags for injection into HTML
  $: configDisableErrorTracking = data.survey?.config?.disableErrorTracking === true;
  $: configEnableSessionReplay = data.survey?.config?.enableSessionReplay === true;
  $: configEnablePerformanceTracking = data.survey?.config?.enablePerformanceTracking === true;
  $: configScript = `<script>
    window.__MOUI_CONFIG__ = {
      disableErrorTracking: ${configDisableErrorTracking},
      enableSessionReplay: ${configEnableSessionReplay},
      enablePerformanceTracking: ${configEnablePerformanceTracking}
    };
  <\/script>`;
</script>

<svelte:head>
  {@html configScript}
</svelte:head>

<MetaTags og={$page.data.og || data.og} domain={data.survey?.config?.deployment?.domain} />
<MetaPixel />

<SharedLayout survey={interpretedSurvey}>
  <div 
    data-theme={themeValue}
    class="survey-wrapper main-content {allowTextSelection ? 'allow-text-selection' : textSelectionClass}"
    class:disabled={($surveyStore?.tokenError && isMainSurveyRoute) || showGdprBanner}
    class:mounted={mounted}
  >
    {#if $surveyStore?.tokenError && isMainSurveyRoute}
      <SystemStatusBanner
        title={connectionErrorMessages.title}
        subtitle={connectionErrorMessages.subtitle}
        ctaLabel={connectionErrorMessages.retryLabel}
        on:retry={handleTokenRetry}
      />
    {/if}
    
    {#if showGdprBanner && currentPageData?.config?.consentPolicy}
      {@const policies = Array.isArray(currentPageData.config.consentPolicy) ? currentPageData.config.consentPolicy : [currentPageData.config.consentPolicy]}
      {@const firstPolicy = policies[0]}
      {#if firstPolicy?.data}
        <GdprConsentBanner
          policyData={firstPolicy.data}
          privacyLink={firstPolicy.privacyLink || `/policies/${firstPolicy.data.policy.slug}`}
          goodbyeUrl={firstPolicy.goodbyeUrl || '/'}
          on:consent={handleGdprConsent}
          on:disagree={e => goto(e.detail.url)}
        />
      {/if}
    {/if}

    {#if showAnalyticsBanner}
      <AnalyticsConsentBanner
        message={analyticsConsentConfig?.message || 'We use cookies to analyze site traffic and improve your experience.'}
        acceptLabel={analyticsConsentConfig?.acceptLabel || 'Accept'}
        rejectLabel={analyticsConsentConfig?.rejectLabel || 'Decline'}
        privacyLink={analyticsConsentConfig?.privacyLink || '/privacy'}
        privacyLinkText={analyticsConsentConfig?.privacyLinkText || 'Privacy Policy'}
        cookieDays={analyticsConsentConfig?.cookieDays || 365}
        on:accept={handleAnalyticsAccept}
        on:reject={handleAnalyticsReject}
      />
    {/if}
    
    <div class="content-wrapper">
      {#if $page.url.pathname.startsWith('/s/') || $page.url.pathname.startsWith('/stats/')}
        <!-- Survey routes and stats dashboard: no transition wrapper -->
        <slot />
      {:else}
        <!-- Other routes: apply page transition -->
        {#if showContent}
          <div
            in:fly="{{ x: 15, duration: 300, delay: 100 }}"
            out:fly="{{ x: -10, duration: 200 }}"
          >
            <slot />
          </div>
        {/if}
      {/if}
    </div>
  </div>
</SharedLayout>

<SurveyWatcher />

<style>
  .main-content {
    height: 100%;
    opacity: 0;
    transition: opacity 0.3s ease-out;
  }

  .main-content.mounted {
    opacity: 1;
  }

  .survey-wrapper {
    position: relative;
  }

  .survey-wrapper.disabled > .content-wrapper {
    pointer-events: none;
    opacity: 0.9;
  }

  .content-wrapper {
    position: relative;
    min-height: 100dvh;
    width: 100%;
  }

  .content-wrapper > div {
    width: 100%;
    height: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    .content-wrapper > div {
      animation: none !important;
      transition: none !important;
    }
  }
</style> 